// components/DeviceGroupsTable/DeviceGroupsTable.tsx
import React, { useRef, useState } from 'react';
import { Table, Button, Space, Empty, Input } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType, ColumnType, TablePaginationConfig } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import type { DeviceGroup } from '../../types';

interface DeviceGroupsTableProps {
  deviceGroups: DeviceGroup[];
  loading?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onChangePage?: (page: number, pageSize: number) => void;

  // Add search and sort props
  nameSearch?: string;
  onSearchName?: (q: string) => void;
  onRequestSort?: (field: string, order: 'ascend' | 'descend' | null) => void;

  onViewDeviceGroup?: (deviceGroup: DeviceGroup) => void;
  onEditDeviceGroup?: (deviceGroup: DeviceGroup) => void;
  onDeleteDeviceGroup?: (deviceGroup: DeviceGroup) => void;
}

const DeviceGroupsTable: React.FC<DeviceGroupsTableProps> = ({
  deviceGroups,
  loading,
  total,
  page = 1,
  pageSize = 10,
  onChangePage,
  nameSearch,
  onSearchName,
  onRequestSort,
  onViewDeviceGroup,
  onEditDeviceGroup,
  onDeleteDeviceGroup,
}) => {
  // Local search state for name column
  const [localNameQuery, setLocalNameQuery] = useState(nameSearch ?? '');
  const searchInputRef = useRef<Input | null>(null);

  // Handle table changes (sorting and pagination)
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
    extra: any
  ) => {

    // ✅ Handle pagination separately from sorting (like DevicesTable)
    if (extra.action === 'paginate') {
      if (pagination.current && pagination.pageSize) {
        onChangePage?.(pagination.current, pagination.pageSize);
      }
      return;
    }

    // ✅ Handle sorting only when action is 'sort'
    if (extra.action === 'sort') {
      if (onRequestSort && sorter) {
        const { field, order } = sorter;
        onRequestSort(field, order);
      }
      return;
    }

    // ✅ Handle filters if needed (future enhancement)
    if (extra.action === 'filter') {
      // Handle filters here if needed
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const columns: ColumnsType<DeviceGroup> = [
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      // Add search dropdown for backend search
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInputRef}
            placeholder="Search group name"
            value={localNameQuery}
            onChange={(e) => setLocalNameQuery(e.target.value)}
            onPressEnter={() => {
              onSearchName?.(localNameQuery);
              confirm();
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                onSearchName?.(localNameQuery);
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                setLocalNameQuery('');
                onSearchName?.('');
                clearFilters?.();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      filteredValue: nameSearch ? [nameSearch] : undefined,
      render: (name: string) => (
        <div className="flex items-center">
          <span className="font-medium">{name}</span>
        </div>
      ),
    },
    {
      title: 'Devices Count',
      dataIndex: 'deviceCount',
      key: 'deviceCount',
      sorter: true, // Enable backend sorting
      render: (count: number) => <span className="font-medium">{count || 0} devices</span>,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true, // Enable backend sorting
      render: (dateString: string) => (
        <span className="text-sm text-gray-600">
          {dateString ? formatDateTime(dateString) : '—'}
        </span>
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true, // Enable backend sorting
      render: (dateString: string) => (
        <span className="text-sm text-gray-600">
          {dateString ? formatDateTime(dateString) : '—'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: DeviceGroup) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewDeviceGroup?.(record)}
            title="View Group"
          />
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEditDeviceGroup?.(record)}
            title="Edit Group"
          />
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => onDeleteDeviceGroup?.(record)}
            danger
            title="Delete Group"
          />
        </Space>
      ),
    },
  ];

  if (!loading && (!deviceGroups || deviceGroups.length === 0)) {
    return (
      <Table
        columns={columns}
        dataSource={[]} // ✅ Empty array instead of hiding table
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} device groups`,
        }}
        scroll={{ x: 800 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No device groups found"
              style={{ padding: '40px 0' }}
            >
              <p style={{ marginBottom: 0, color: '#666' }}>
                Try adjusting your search filters or add a new device group to get started.
              </p>
            </Empty>
          ),
        }}
      />
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={deviceGroups}
      rowKey="id"
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} device groups`,
      }}
      scroll={{ x: 800 }}
    />
  );
};

export default DeviceGroupsTable;
